import React, { useState, useEffect } from 'react';
import {
    Box,
    Avatar,
    Typography,
    IconButton,
    Paper,
    Stack,
    Link,
} from '@mui/material';
import {
    Call as CallIcon,
    CallEnd as CallEndIcon,
} from '@mui/icons-material';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
    setCloseCall,
    setEndCall,
} from 'App/store/actions/call.action';

interface MobileCallProps {
    call: any;
    setCloseCall: () => void;
    setEndCall: () => void;
    user: any;
    windowToggle: (show: boolean) => void;
    callActive: any;
}

const MobileCall: React.FC<MobileCallProps> = ({
    call,
    setCloseCall,
    setEndCall,
    user,
    windowToggle,
    callActive,
}) => {
    const [callDuration, setCallDuration] = useState<number>(0);
    const [isDialing, setIsDialing] = useState<boolean>(false);

    // Timer for call duration when call is active
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (callActive === 'started') {
            interval = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [callActive]);

    useEffect(() => {
        if (!call) {
            setCallDuration(0);
            windowToggle(false);
        } else {
            windowToggle(true);
        }

        return () => {
            setCallDuration(0);
            windowToggle(false);
        };
    }, [call]);

    // Format call duration
    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleMobileCall = () => {
        if (call) {
            const phoneNumber = user.type === 'rider'
                ? call.to.mobile
                : call.from.mobile;

            // Create tel: link for mobile call
            const telLink = `tel:${phoneNumber}`;

            // Open the phone dialer
            window.open(telLink, '_self');

            setIsDialing(true);

            // Simulate call duration tracking
            setTimeout(() => {
                if (callActive !== 'started') {
                    // Simulate call started after 3 seconds
                    setCallDuration(0);
                }
            }, 3000);
        }
    };

    const endCall = () => {
        closeCall();
    };

    const closeCall = () => {
        setCallDuration(0);
        setIsDialing(false);
        setCloseCall();
    };

    if (!call) return null;

    const contactInfo = user.type === 'rider' ? call.to : call.from;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor: '#f5f5f5',
                p: 3,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    maxWidth: 400,
                    width: '100%',
                }}
            >
                <Avatar
                    src={contactInfo.img}
                    sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 2,
                    }}
                />

                <Typography variant="h5" gutterBottom>
                    {contactInfo.name}
                </Typography>

                <Typography variant="body1" color="text.secondary" gutterBottom>
                    {contactInfo.mobile}
                </Typography>

                {callActive === 'started' && (
                    <Typography variant="h6" color="primary" mb={2}>
                        Call Duration: {formatDuration(callDuration)}
                    </Typography>
                )}

                {isDialing && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Dialing...
                    </Typography>
                )}

                <Stack direction="row" spacing={3} justifyContent="center" mt={3}>
                    <IconButton
                        onClick={handleMobileCall}
                        sx={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            width: 60,
                            height: 60,
                            '&:hover': {
                                backgroundColor: '#45a049',
                            },
                        }}
                    >
                        <CallIcon />
                    </IconButton>
                    <IconButton
                        onClick={endCall}
                        sx={{
                            backgroundColor: '#F44336',
                            color: 'white',
                            width: 60,
                            height: 60,
                            '&:hover': {
                                backgroundColor: '#da190b',
                            },
                        }}
                    >
                        <CallEndIcon />
                    </IconButton>
                </Stack>

                <Box mt={2}>
                    <Link
                        href={`tel:${contactInfo.mobile}`}
                        underline="hover"
                        color="primary"
                        variant="body2"
                    >
                        Tap to call {contactInfo.mobile}
                    </Link>
                </Box>
            </Paper>
        </Box>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
    setCloseCall: () => dispatch(setCloseCall()),
    setEndCall: () => dispatch(setEndCall()),
});

const mapStateToProps = (state: any) => ({
    callActive: state.call.callActive,
    call: state.call.call,
    user: state.user.user,
});

export default connect(mapStateToProps, mapDispatchToProps)(MobileCall);